import { emitReplayStepSounds } from './ReplayVolume.js'

const POST_END_COSMETIC_MS = 1000

export function createReplayLoop(o) {
  let playing = false
  const api = { speedMode: 'normal', playAccumulator: 0 }
  let raf = 0
  let lastFrameMs = 0
  let postEndCosmeticUntilMs = null

  function stopLoop() {
    postEndCosmeticUntilMs = null
    o.playback.world?.setPostGameVisual(false)
    if (raf) cancelAnimationFrame(raf)
    raf = 0
    lastFrameMs = 0
    api.playAccumulator = 0
  }

  function clearSpeedToggleUi() {
    o.btnRewind.setAttribute('aria-pressed', 'false')
    o.btnFf.setAttribute('aria-pressed', 'false')
  }

  function finishPlaybackAtEnd() {
    api.playAccumulator = 0
    o.playback.world?.setPostGameVisual(false)
    o.audio.setThruster(false)
    playing = false
    api.speedMode = 'normal'
    o.updateTransportButton()
    clearSpeedToggleUi()
  }

  function pausePlayback() {
    o.audio.setThruster(false)
    playing = false
    api.speedMode = 'normal'
    stopLoop()
    o.updateTransportButton()
    clearSpeedToggleUi()
  }

  function loopFrame(ms) {
    if (postEndCosmeticUntilMs != null && ms >= postEndCosmeticUntilMs) {
      postEndCosmeticUntilMs = null
      lastFrameMs = 0
      api.playAccumulator = 0
      raf = 0
      o.playback.world?.setPostGameVisual(false)
      o.updateTimelineUi()
      o.updateTransportButton()
      o.render()
      return
    }

    if (!playing && postEndCosmeticUntilMs == null) {
      raf = 0
      return
    }
    raf = requestAnimationFrame(loopFrame)

    const tickMs = o.playback.tickMs
    if (!lastFrameMs) lastFrameMs = ms
    const dt = ms - lastFrameMs
    lastFrameMs = ms

    if (api.speedMode === 'rw2') {
      api.playAccumulator += dt * 2
      while (api.playAccumulator >= tickMs && o.playback.appliedSteps > 0) {
        const next = Math.max(0, o.playback.appliedSteps - 1)
        o.playback.seek(next)
        api.playAccumulator -= tickMs
      }
    } else {
      const mult = api.speedMode === 'ff2' ? 2 : 1
      api.playAccumulator += dt * mult
      while (api.playAccumulator >= tickMs && o.playback.appliedSteps < o.duration) {
        const idx = o.playback.appliedSteps
        const row = o.playback.decoded.ticks[idx]
        const snapBefore = o.playback.getSnapshot()
        o.playback.stepTick(idx)
        api.playAccumulator -= tickMs
        const snapAfter = o.playback.getSnapshot()
        emitReplayStepSounds(snapBefore, snapAfter, {
          replayMuted: o.getReplayMuted(),
          toroidalMap: o.toroidalMap,
          audio: o.audio,
          particles: o.particles,
          getFollowShip: o.getFollowShip,
          row,
          followOrder: o.followOrder,
          followIndex: o.getFollowIndex(),
          rosterIds: o.playback.decoded.rosterIds
        })
        if (o.playback.appliedSteps >= o.duration) {
          postEndCosmeticUntilMs = performance.now() + POST_END_COSMETIC_MS
          finishPlaybackAtEnd()
          break
        }
      }
    }

    // FF at last tick: inner `while` does not run (`appliedSteps < duration` is false); still need end UI + post-roll.
    if (playing && api.speedMode === 'ff2' && o.playback.appliedSteps >= o.duration) {
      postEndCosmeticUntilMs = performance.now() + POST_END_COSMETIC_MS
      finishPlaybackAtEnd()
    } else if (playing && api.speedMode === 'rw2' && o.playback.appliedSteps <= 0) {
      pausePlayback()
    }

    o.updateTimelineUi()
    o.updateTransportButton()
    o.render()
  }

  function resumePlaybackAfterScrub() {
    void (async () => {
      await o.audio.resume()
      o.audio.setThruster(false)
      postEndCosmeticUntilMs = null
      playing = true
      lastFrameMs = 0
      if (!raf) raf = requestAnimationFrame(loopFrame)
      o.updateTransportButton()
    })()
  }

  function scheduleFrame() {
    if (!raf) raf = requestAnimationFrame(loopFrame)
  }

  Object.assign(api, {
    stopLoop,
    pausePlayback,
    resumePlaybackAfterScrub,
    scheduleFrame,
    resetLastFrameMs() {
      lastFrameMs = 0
    }
  })
  Object.defineProperty(api, 'playing', {
    enumerable: true,
    get() {
      return playing
    },
    set(v) {
      if (v) postEndCosmeticUntilMs = null
      playing = v
    }
  })
  return api
}
